import './_file-uploader.scss';
import './_file-uploader.dark-mode.scss';
import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import Loader from './Loader';

import { returnFileSize, formatedFileName, isTokenExpired, uuid } from './helpers';
import { tryToParseToJson } from '../../utils/helper';
import { getCDNToken } from '../../services/endpoints/general';
import { uploadFileToCdn } from '../../services/endpoints/cdn';

const ACCEPT_FORMATS_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'text/plain', 'text/csv', 'application/pdf'];
const MAX_FILE_SIZE = 5242880;
const MAX_FILES_LENGTH = 5;
let cancelToken = axios.CancelToken.source();

const FilesUploader = forwardRef(({ cdn, onChange, requestUrl }, ref) => {
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const filesProgress = useRef({});
  const replaceMode = useRef(false);

  const [cdnInfo, setCdnInfo] = useState(cdn);
  const [filesList, setFilesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingValue, setLoadingValue] = useState(0);

  const onRemoveFile = (file) => {
    setFilesList((prevState) => prevState.filter((f) => {
      if (file.file_id === f.file_id && file.name === f.name) {
        return false;
      }
      return true;
    }));
  };

  const onUploadProgress = (file, filesLength) => (progressEvent) => {
    filesProgress.current[file.name] = (progressEvent.loaded / progressEvent.total) * 100;
    const totalPercent = Object.values(filesProgress.current).reduce((sum, num) => sum + num, 0);
    if (filesLength > 0) {
      setLoadingValue(Math.floor((totalPercent / (filesLength * 100)) * 100));
    }
  };

  const onSendFile = async (file, filesLength, token) => {
    if (!ACCEPT_FORMATS_TYPES.includes(file.type)) {
      return { file_id: uuid(), error: 'Invalid file format' };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { file_id: uuid(), error: 'File is too large (limit 5 MB)' };
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadFileToCdn(requestUrl, formData, {
        headers: {
          'x-api-key': token,
        },
        onUploadProgress: onUploadProgress(file, filesLength),
        cancelToken: cancelToken.token,
      });

      if (response.data.success) {
        return { ...response.data?.data, name: file.name } || { file_id: uuid(), error: 'Internal error' };
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (error?.response?.data?.error_message) {
        const errorData = tryToParseToJson(error.response.data.error_message);
        if (errorData) {
          return {
            file_id: uuid(),
            error: errorData?.code === 1 ? 'Invalid file format' : errorData?.code === 2 ? 'File is too large (limit 5 MB)' : 'Internal error',
          };
        }
      }
      return { file_id: uuid(), error: 'Internal error' };
    }
  };

  const onFilesChange = async (event) => {
    if (event.target.files.length) {
      let tempFilesList = filesList;
      if (replaceMode.current) {
        onRemoveFile(replaceMode.current);
        tempFilesList = tempFilesList.filter((f) => {
          if (replaceMode.current.file_id === f.file_id && replaceMode.current.name === f.name) {
            return false;
          }
          return true;
        });
      }

      let files = [...event.target.files].filter((file) => {
        const findedInState = tempFilesList.find((f) => file.name === f.name && file.size === f.size && file.type === f.mime);
        return !findedInState;
      });

      const filesLengthMax = MAX_FILES_LENGTH - tempFilesList.length;
      files = files.slice(0, filesLengthMax);

      let filesLength = files.length;
      files.forEach((file) => {
        if (!ACCEPT_FORMATS_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
          filesLength -= 1;
        }
      });
      if (event?.target?.value) event.target.value = null;

      let token = cdnInfo?.token;
      if (isTokenExpired(cdnInfo?.exp)) {
        setIsLoading(true);
        const response = await getCDNToken();
        token = response.data.token;
        dispatch({ type: '@@general/SET_CDN_DATA', payload: response.data });
        setCdnInfo(response.data);
      }

      const requests = [];
      for (let index = 0; index < files.length; index++) {
        requests.push(onSendFile(files[index], filesLength, token));
      }

      setIsLoading(true);
      try {
        const files = await Promise.all(requests);
        setIsLoading(false);
        setLoadingValue(0);
        setFilesList((prevState) => [...prevState, ...files]);
        filesProgress.current = {};
      } catch (error) {
        setIsLoading(false);
        setLoadingValue(0);
        filesProgress.current = {};
      }
    }
  };

  const onClickUploadFiles = () => {
    replaceMode.current = false;
    inputRef?.current?.click();
  };

  const onItemFileClick = (event, file) => {
    event.preventDefault();
    if (isLoading) return;
    replaceMode.current = file;
    inputRef?.current?.click();
  };

  const onStopUpload = () => {
    cancelToken.cancel('Cancel request');
    cancelToken = axios.CancelToken.source();
    setIsLoading(false);
    filesProgress.current = {};
  };

  useImperativeHandle(ref, () => ({
    onFilesChange,
  }));

  useEffect(
    () => () => {
      cancelToken.cancel('Cancel request');
      cancelToken = axios.CancelToken.source();
    },
    [],
  );

  useEffect(() => {
    onChange?.(filesList);
  }, [filesList, onChange]);

  if (!requestUrl || !cdn || isEmpty(cdn)) {
    return null;
  }

  return (
    <div className="files-wrapper">
      {!isLoading && !filesList?.length ? (
        <div className="files-upload" onClick={onClickUploadFiles}>
          <span className="fas fa-paperclip" />
          <span>Attach files</span>
        </div>
      ) : null}

      {isLoading && (
        <div className="files-loader">
          <span className="fas fa-paperclip" />
          <Loader value={loadingValue} />
          <span className="fas fa-times files-stop" onClick={() => onStopUpload()} />
        </div>
      )}

      {filesList?.length ? (
        <div className="files-preview">
          {filesList.map((file) => (
            <div className="files-preview__item" key={file.file_id + file.name}>
              <span className="fas fa-paperclip" />
              {file.error ? (
                <>
                  {' '}
                  <span>{file.error}</span>
                  <span className="fas fa-times files-error__clear" onClick={() => onRemoveFile(file)} />
                </>
              ) : (
                <>
                  <a href="# " className="file-name" title={file.name} onClick={(e) => onItemFileClick(e, file)}>
                    {formatedFileName(file.name)}
                  </a>
                  ,
                  {' '}
                  <span>{returnFileSize(file.size)}</span>
                  <span className="fas fa-times file-remove" onClick={() => onRemoveFile(file)} />
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <input type="file" className="hidden" multiple accept={ACCEPT_FORMATS_TYPES.join(',')} ref={inputRef} onChange={onFilesChange} />
    </div>
  );
});

FilesUploader.propTypes = {
  cdn: PropTypes.object,
  onChange: PropTypes.func,
  requestUrl: PropTypes.string,
};

export default FilesUploader;
